/**
 * Edit Profile Page
 *
 * Allows users to edit their profile information
 * Features a two-column layout with form sections on the left and tips on the right
 *
 * INTEGRATED WITH:
 * - Auth store for user data (no extra API call needed!)
 * - useUpdateProfile hook for saving changes
 * - Inline photo upload functionality in accordion
 */

import {
  closestCenter,
  DndContext,
  type DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core"
import {
  arrayMove,
  rectSortingStrategy,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
} from "@dnd-kit/sortable"
import { CSS } from "@dnd-kit/utilities"
import { ArrowUpToLine, Loader2 } from "lucide-react"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useLocation, useNavigate } from "react-router-dom"
import { toast } from "sonner"
import type { LocationData } from "@/components/forms/autoCompleteInput"
import { AutocompleteInput } from "@/components/forms/autoCompleteInput"
import { CustomInput } from "@/components/forms/customInput"
import { CustomTextarea } from "@/components/forms/customTextarea"
import { TagsInput } from "@/components/forms/tagsInput"
import { PencilEditIcon, TrashDeleteIcon } from "@/components/icons"
import { ImageCropperModal } from "@/components/media/ImageCropperModal"
import { MediaUploadModal } from "@/components/media/MediaUploadModal"
import { MediaPreview } from "@/components/media/mediaPreview"
import { VideoUploadModal } from "@/components/media/VideoUploadModal"
import { LeaveConfirmationModal } from "@/components/modals/LeaveConfirmationModal"
import { ProfileCompletionTips } from "@/components/profile/ProfileCompletionTips"
import { ProfileImageUpload } from "@/components/profile/ProfileImageUpload"
import { ProfileActionButtons } from "@/components/profile/profileActionButtons"
import type { AccordionItem } from "@/components/ui/accordion"
import { Accordion } from "@/components/ui/accordion"
import { Button } from "@/components/ui/button"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { PROFILE_MEDIA_LIMITS, PROFILE_PHOTO_LIMITS } from "@/constants/media"
import { useUpdateProfile } from "@/features/auth/hooks/useAuth"
import type { ProfilePhoto } from "@/features/auth/store/authStore"
import { useAuthStore } from "@/features/auth/store/authStore"
import { ProfileVideoPlayer } from "@/features/profile/components/ProfileVideoPlayer"
import { useChangeVideo } from "@/features/profile/hooks/useChangeVideo"
import { useDeletePhoto } from "@/features/profile/hooks/useDeletePhoto"
import { useDeleteVideo } from "@/features/profile/hooks/useDeleteVideo"
import { useEditProfile } from "@/features/profile/hooks/useEditProfile"
import { useEthnicBackgrounds } from "@/features/profile/hooks/useEthnicBackgrounds"
import { useIntentions } from "@/features/profile/hooks/useIntentions"
import { useProfilePhotos } from "@/features/profile/hooks/useProfilePhotos"
import { useProfileVideo } from "@/features/profile/hooks/useProfileVideo"
import { useReplaceProfilePhoto } from "@/features/profile/hooks/useReplaceProfilePhoto"
import { useSetCoverPhoto } from "@/features/profile/hooks/useSetCoverPhoto"
import { useSpiritualTraditions } from "@/features/profile/hooks/useSpiritualTraditions"
import { useUploadPhoto } from "@/features/profile/hooks/useUploadPhoto"
import { useUploadVideo } from "@/features/profile/hooks/useUploadVideo"
import type { EditProfileFormData } from "@/features/profile/schemas/edit-profile.schema"
import type { PrivacySettings } from "@/features/profile/types/profile.types"
import {
  getIntentionsSummary,
  getTraditionsSummary,
  validateIntentionsSection,
  validatePhotosSection,
  validatePrivacySection,
  validateProfileInfoSection,
  validateTraditionsSection,
} from "@/features/profile/utils/accordionValidation"
import { useUnsavedChangesGuard } from "@/hooks/useUnsavedChangesGuard"
import { useTranslation } from "@/language"

// Sortable wrapper for gallery photos in the grid
function SortablePhotoItem({
  photo,
  onRemove,
  onSetCover,
  onEdit,
}: {
  photo: { id: string; src: string; isCover?: boolean }
  onRemove: (id: string) => void
  onSetCover: (id: string) => void
  onEdit: (photo: { id: string; src: string }) => void
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: photo.id,
  })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  }

  return (
    <div ref={setNodeRef} style={style}>
      <MediaPreview
        type="image"
        src={photo.src}
        isCover={photo.isCover}
        isDragging={isDragging}
        dragHandleProps={{ ...attributes, ...listeners }}
        variant="editProfile"
        onRemove={() => onRemove(photo.id)}
        onCoverSelect={() => onSetCover(photo.id)}
        onEditImage={() => onEdit(photo)}
      />
    </div>
  )
}

export function EditProfileScreen() {
  const navigate = useNavigate()
  const location = useLocation()
  const { t } = useTranslation("profile")
  const { user } = useAuthStore()

  // Parse tab from URL hash (e.g., #tab=photos)
  const defaultOpenIndex = useMemo(() => {
    const hash = location.hash
    if (hash.startsWith("#tab=")) {
      const tab = hash.slice(5) // Remove "#tab="
      const tabIndexMap: Record<string, number> = {
        "profile-info": 0,
        photos: 1,
        traditions: 2,
        intentions: 3,
        privacy: 4,
      }
      return tabIndexMap[tab] ?? 0
    }
    return 0
  }, [location.hash])

  // Scroll to accordion section when navigating from hash
  useEffect(() => {
    if (location.hash.startsWith("#tab=")) {
      const timer = setTimeout(() => {
        const el = document.getElementById("edit-profile-accordion")
        if (el) {
          el.scrollIntoView({ behavior: "smooth", block: "start" })
        }
      }, 300)
      return () => clearTimeout(timer)
    }
  }, [location.hash])

  // Use the update profile hook from auth hooks
  const updateProfileMutation = useUpdateProfile()

  // Local state
  const [tags, setTags] = useState<string[]>([])
  const [removedTags, setRemovedTags] = useState<string[]>([])
  const [locationData, setLocationData] = useState<LocationData | null>(null)

  // State for new profile sections (multi-select for traditions and backgrounds)
  const [selectedTraditions, setSelectedTraditions] = useState<
    Array<{ id: string; title: string; is_custom?: boolean }>
  >([])
  const [selectedBackgrounds, setSelectedBackgrounds] = useState<
    Array<{ id: string; title: string; is_custom?: boolean }>
  >([])
  // Track removed traditions and backgrounds for soft delete
  const [removedTraditions, setRemovedTraditions] = useState<string[]>([])
  const [removedBackgrounds, setRemovedBackgrounds] = useState<string[]>([])
  // State for "Others" text inputs (array for multiple custom entries)
  const [otherTraditions, setOtherTraditions] = useState<string[]>([])
  const [otherBackgrounds, setOtherBackgrounds] = useState<string[]>([])
  const [selectedIntentions, setSelectedIntentions] = useState<string[]>([])
  const [removedIntentions, setRemovedIntentions] = useState<string[]>([])
  const [privacySettings, setPrivacySettings] = useState<PrivacySettings>({
    practiceRitualsVisibilityAccess: "everyone",
    teachingRitualsVisibilityAccess: "everyone",
    attendedEventsVisibilityAccess: "everyone",
    statsVisibilityAccess: "everyone",
    coachVisibilityAccess: "everyone",
  })

  // Photo section state and hooks
  const fileInputRef = useRef<HTMLInputElement>(null)
  const coverInputRef = useRef<HTMLInputElement>(null)
  const [isUploading, setIsUploading] = useState(false)
  const [isCoverUploading, setIsCoverUploading] = useState(false)
  const [uploadModalOpen, setUploadModalOpen] = useState(false)
  const [coverCropperModalOpen, setCoverCropperModalOpen] = useState(false)
  const [selectedCoverFile, setSelectedCoverFile] = useState<File | null>(null)
  const [croppingPhoto, setCroppingPhoto] = useState<{ id: string; src: string } | null>(null)
  const { data: serverPhotos = [], isLoading: photosLoading } = useProfilePhotos(user?.id || "")
  const uploadPhoto = useUploadPhoto()
  const deletePhoto = useDeletePhoto()
  const replacePhoto = useReplaceProfilePhoto()
  const setCoverPhoto = useSetCoverPhoto()

  // Transform server photos to component format
  const photos: Array<{ id: string; src: string; isCover?: boolean }> = serverPhotos.map(
    (photo: ProfilePhoto) => ({
      id: photo.id,
      src: photo.filePath,
      isCover: photo.isCover,
    }),
  )

  // Filter photos into cover and gallery
  const coverPhotos = photos.filter(p => p.isCover)
  const galleryPhotos = photos.filter(p => !p.isCover)

  // Local gallery order for drag-to-reorder.
  // Uses a ref to track user-initiated drags so we can re-sync from
  // server data when new uploads arrive (with real DB IDs) without
  // losing the user's manual reorder.
  const [galleryOrder, setGalleryOrder] = useState<string[]>([])
  const userDraggedRef = useRef(false)

  // Memoize server photo IDs so the effect only fires when the actual
  // set of photos changes (not on every re-render where galleryPhotos
  // is a new array reference from .map().filter())
  const serverPhotoIdKey = galleryPhotos.map(p => p.id).join(",")
  const serverPhotoIds = useMemo(() => {
    if (serverPhotoIdKey === "") return []
    return serverPhotoIdKey.split(",")
  }, [serverPhotoIdKey])

  // Re-sync galleryOrder from server data when photos change
  // (new uploads bring real IDs, or photos deleted)
  useEffect(() => {
    if (serverPhotoIds.length === 0) {
      setGalleryOrder([])
      userDraggedRef.current = false
      return
    }
    if (!userDraggedRef.current) {
      setGalleryOrder(serverPhotoIds)
    } else {
      setGalleryOrder(prev => {
        const existingSet = new Set(prev)
        const newIds = serverPhotoIds.filter(id => !existingSet.has(id))
        if (newIds.length === 0) return prev
        return [...prev, ...newIds]
      })
    }
  }, [serverPhotoIds])

  // Sort gallery by local order
  const orderedGallery = useMemo(() => {
    if (galleryOrder.length === 0) return galleryPhotos
    const orderMap = new Map(galleryOrder.map((id, idx) => [id, idx]))
    return [...galleryPhotos].sort(
      (a, b) => (orderMap.get(a.id) ?? 999) - (orderMap.get(b.id) ?? 999),
    )
  }, [galleryPhotos, galleryOrder])

  // dnd-kit sensors for photo reordering
  const photoSensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 8 },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  )

  const handlePhotoDragEnd = (event: DragEndEvent) => {
    const { active, over } = event
    if (!over || active.id === over.id) return
    const newOrder = arrayMove(
      galleryOrder,
      galleryOrder.indexOf(active.id as string),
      galleryOrder.indexOf(over.id as string),
    )
    userDraggedRef.current = true
    setGalleryOrder(newOrder)
  }

  const handleDeletePhoto = useCallback(
    (photoId: string) => {
      if (!user?.id) return
      deletePhoto.mutate(
        { userId: user.id, photoId },
        {
          onSuccess: () => {
            toast.success(t("editProfile.toast.photoDeleted"))
          },
          onError: () => {
            toast.error(t("editProfile.toast.photoDeleteFailed"))
          },
        },
      )
    },
    [user, deletePhoto, t],
  )

  const handleEditGalleryPhoto = useCallback((photo: { id: string; src: string }) => {
    if (!photo.src) return
    setCroppingPhoto({ id: photo.id, src: photo.src })
  }, [])

  const handleGalleryCropComplete = useCallback(
    async (croppedFile: File) => {
      if (!croppingPhoto || !user?.id) return
      const { id } = croppingPhoto
      replacePhoto.mutate(
        { userId: user.id, photoId: id, file: croppedFile },
        {
          onSuccess: () => {
            toast.success("Photo updated successfully")
          },
          onError: () => {
            toast.error("Failed to update photo")
          },
        },
      )
      setCroppingPhoto(null)
    },
    [croppingPhoto, user?.id, replacePhoto],
  )

  const handleSetCover = useCallback(
    (photoId: string) => {
      if (!user?.id) return
      setCoverPhoto.mutate(
        { userId: user.id, photoId },
        {
          onSuccess: () => {
            toast.success(t("editProfile.toast.coverUpdated"))
          },
          onError: () => {
            toast.error(t("editProfile.toast.coverUpdateFailed"))
          },
        },
      )
    },
    [user, setCoverPhoto, t],
  )

  const handleModalUpload = useCallback(
    async (files: File[]) => {
      if (!user?.id) return

      for (const file of files) {
        setIsUploading(true)
        await uploadPhoto.mutateAsync(
          { userId: user.id, file },
          {
            onSuccess: () => {
              toast.success(t("editProfile.toast.photoUploaded"))
            },
            onError: () => {
              toast.error(t("editProfile.toast.photoUploadFailed"))
            },
          },
        )
      }
      setIsUploading(false)
    },
    [user, uploadPhoto, t],
  )

  const handleFileUpload = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (!user?.id) return
      const files = e.target.files
      if (!files) return

      const filesArray = Array.from(files)

      // Check gallery photos limit (exclude cover photos)
      if (galleryPhotos.length + filesArray.length > PROFILE_PHOTO_LIMITS.MAX_GALLERY_PHOTOS) {
        toast.error(
          t("editProfile.toast.maxPhotosError", { max: PROFILE_PHOTO_LIMITS.MAX_GALLERY_PHOTOS }),
        )
        return
      }

      filesArray.forEach(file => {
        if (!PROFILE_PHOTO_LIMITS.ALLOWED_TYPES.includes(file.type as never)) {
          toast.error(t("editProfile.toast.invalidFileType"))
          return
        }

        if (file.size > PROFILE_PHOTO_LIMITS.MAX_FILE_SIZE) {
          toast.error(t("editProfile.toast.fileSizeError"))
          return
        }

        setIsUploading(true)
        uploadPhoto.mutate(
          { userId: user.id, file },
          {
            onSuccess: () => {
              toast.success(t("editProfile.toast.photoUploaded"))
            },
            onError: () => {
              toast.error(t("editProfile.toast.photoUploadFailed"))
            },
            onSettled: () => {
              setIsUploading(false)
            },
          },
        )
      })

      if (fileInputRef.current) {
        fileInputRef.current.value = ""
      }
    },
    [user, uploadPhoto, galleryPhotos, t],
  )

  const handleUploadClick = useCallback(() => {
    setUploadModalOpen(true)
  }, [])

  const handleCoverUploadClick = useCallback(() => {
    coverInputRef.current?.click()
  }, [])

  const handleCoverCropperComplete = useCallback(
    async (croppedFile: File) => {
      if (!user?.id) return

      setIsCoverUploading(true)
      await uploadPhoto.mutateAsync(
        { userId: user.id, file: croppedFile, isCover: true },
        {
          onSuccess: () => {
            toast.success(t("editProfile.toast.coverPhotoUploaded"))
            setCoverCropperModalOpen(false)
            setSelectedCoverFile(null)
          },
          onError: () => {
            toast.error(t("editProfile.toast.coverPhotoUploadFailed"))
          },
        },
      )
      setIsCoverUploading(false)
    },
    [user, uploadPhoto, t],
  )

  const handleCoverUpload = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = e.target.files
      if (!files || files.length === 0) return

      const file = files[0]

      if (!PROFILE_PHOTO_LIMITS.ALLOWED_TYPES.includes(file.type as never)) {
        toast.error(t("editProfile.toast.invalidFileType"))
        return
      }

      if (file.size > PROFILE_PHOTO_LIMITS.MAX_FILE_SIZE) {
        toast.error(t("editProfile.toast.fileSizeError"))
        return
      }

      // Open cropper modal with the selected file
      setSelectedCoverFile(file)
      setCoverCropperModalOpen(true)

      if (coverInputRef.current) {
        coverInputRef.current.value = ""
      }
    },
    [t],
  )

  // Video section state and hooks
  const [videoUploadModalOpen, setVideoUploadModalOpen] = useState(false)
  const [isVideoUploading, setIsVideoUploading] = useState(false)
  const [isChangingVideo, setIsChangingVideo] = useState(false)
  const {
    data: serverVideo,
    isLoading: videoLoading,
    isError: videoLoadError,
  } = useProfileVideo(user?.id || "")
  // Surface a fetch failure so the owner doesn't mistake it for "no video" and
  // re-upload a duplicate (the empty state otherwise looks identical).
  useEffect(() => {
    if (videoLoadError) toast.error("Couldn't load your video. Refresh to retry.")
  }, [videoLoadError])
  const uploadVideo = useUploadVideo()
  const deleteVideo = useDeleteVideo()
  const changeVideo = useChangeVideo()

  // The native profile video row (or null). Playback is via ProfileVideoPlayer,
  // which handles ready/processing/failed states -- there's no plain URL.
  const video = serverVideo ?? null

  const handleDeleteVideo = useCallback(() => {
    if (!user?.id || !video) return
    deleteVideo.mutate(
      { userId: user.id, videoId: video.id },
      {
        onSuccess: () => {
          toast.success(t("editProfile.toast.videoDeleted"))
        },
        onError: () => {
          toast.error(t("editProfile.toast.videoDeleteFailed"))
        },
      },
    )
  }, [user, deleteVideo, video, t])

  const handleModalVideoUpload = useCallback(
    (file: File) => {
      if (!user?.id) return

      if (video) {
        setIsChangingVideo(true)
        changeVideo.mutate(
          { userId: user.id, file },
          {
            onSuccess: () => {
              toast.success(t("editProfile.toast.videoUpdated"))
            },
            onError: () => {
              toast.error(t("editProfile.toast.videoUpdateFailed"))
            },
            onSettled: () => {
              setIsChangingVideo(false)
            },
          },
        )
      } else {
        setIsVideoUploading(true)
        uploadVideo.mutate(
          { userId: user.id, file },
          {
            onSuccess: () => {
              toast.success(t("editProfile.toast.videoUploaded"))
              // The native row is `pending` here; useProfileVideo polls until
              // it's ready and the player renders it.
            },
            onError: () => {
              toast.error(t("editProfile.toast.videoUploadFailed"))
            },
            onSettled: () => {
              setIsVideoUploading(false)
            },
          },
        )
      }
    },
    [user, video, uploadVideo, changeVideo, t],
  )

  const handleVideoUploadClick = useCallback(() => {
    setVideoUploadModalOpen(true)
  }, [])

  const handleEditVideoClick = useCallback(() => {
    setVideoUploadModalOpen(true)
  }, [])

  // Section validation state for accordion
  const [sectionValidation, setSectionValidation] = useState<{
    profileInfo: { isComplete: boolean; summary?: string }
    photos: { isComplete: boolean; summary?: string }
    traditions: { isComplete: boolean; summary?: string }
    intentions: { isComplete: boolean; summary?: string }
    privacy: { isComplete: boolean; summary?: string }
  }>({
    profileInfo: { isComplete: false },
    photos: { isComplete: false },
    traditions: { isComplete: false },
    intentions: { isComplete: false },
    privacy: { isComplete: false },
  })

  // Inline validation errors for accordion sections
  const [sectionErrors, setSectionErrors] = useState({
    photos: "",
    traditions: "",
    intentions: "",
  })

  // Initialize default values from auth store user data
  // Wrap in useCallback to prevent infinite loop in useEffect
  const getDefaultValues = useCallback(
    (): Partial<EditProfileFormData> => ({
      name: user?.name || "",
      username: user?.username || "",
      city: user?.city || "",
      // Location fields from Google Places
      streetAddress: user?.street_address || "",
      state: user?.state || "",
      country: user?.country || "",
      zipCode: user?.zip_code || "",
      latitude: user?.latitude,
      longitude: user?.longitude,
      bio: user?.bio || "",
      tags: user?.tags?.map(t => t.name) || [],
      website: user?.website_url || "",
      twitterUrl: user?.twitter_url || "",
      instagramUrl: user?.instagram_url || "",
      facebookUrl: user?.facebook_url || "",
    }),
    [user],
  )

  // Form submission handler with our custom logic
  // Declare BEFORE useEditProfile to avoid "used before declaration" error
  const handleProfileSubmit = async (data: EditProfileFormData) => {
    if (!user?.id) {
      return
    }

    // Call the updateProfile mutation with all form data
    updateProfileMutation.mutate(
      {
        name: data.name,
        username: data.username,
        city: data.city || locationData?.city,
        streetAddress: data.streetAddress,
        street_address: data.streetAddress, // Backend format
        state: data.state,
        country: data.country,
        zipcode: data.zipCode,
        zip_code: data.zipCode, // Backend format
        latitude: data.latitude,
        longitude: data.longitude,
        bio: data.bio,
        website_url: data.website || null,
        twitter_url: data.twitterUrl || null,
        instagram_url: data.instagramUrl || null,
        facebook_url: data.facebookUrl || null,
        tags: tags,
        removed_tags: removedTags,
        // New fields for spiritual traditions (multi-select)
        spiritual_traditions: selectedTraditions.map(t => t.id),
        removed_spiritual_traditions: removedTraditions,
        other_spiritual_tradition: otherTraditions.length > 0 ? otherTraditions : null,
        // New fields for ethnic backgrounds (multi-select)
        ethnic_backgrounds: selectedBackgrounds.map(b => b.id),
        removed_ethnic_backgrounds: removedBackgrounds,
        other_ethnic_background: otherBackgrounds.length > 0 ? otherBackgrounds : null,
        // New fields for intentions
        intentions: selectedIntentions.filter(i => !removedIntentions.includes(i)),
        removed_intentions: removedIntentions,
        // Privacy settings
        practice_rituals_visibility_access: privacySettings.practiceRitualsVisibilityAccess,
        teaching_rituals_visibility_access: privacySettings.teachingRitualsVisibilityAccess,
        attended_events_visibility_access: privacySettings.attendedEventsVisibilityAccess,
        stats_visibility_access: privacySettings.statsVisibilityAccess,
        coach_visibility_access: privacySettings.coachVisibilityAccess,
        // Photo order (only send if user reordered)
        ...(galleryOrder.length > 0 ? { photo_order: galleryOrder } : {}),
      },
      {
        onSuccess: () => {
          setRemovedTags([])
          setRemovedIntentions([])
          setRemovedTraditions([])
          setRemovedBackgrounds([])
          // Saved -- let the post-save navigation bypass the guard.
          unsavedGuard.allowNavigation()
          navigate("/profile")
        },
      },
    )
  }

  // ALWAYS call useEditProfile before any early returns (React Hooks rule)
  const { form, handleSubmit } = useEditProfile(handleProfileSubmit, getDefaultValues())

  // Warn before leaving with unsaved edits or an in-flight photo/video upload.
  const unsavedGuard = useUnsavedChangesGuard(
    form.formState.isDirty ||
      isVideoUploading ||
      isChangingVideo ||
      isUploading ||
      isCoverUploading,
  )

  // Initialize form data from auth store user
  useEffect(() => {
    if (user) {
      setTags(user.tags?.map(t => t.name) || [])

      // Initialize spiritual traditions from user data (multi-select)
      if (user.spiritual_traditions && user.spiritual_traditions.length > 0) {
        const traditions = user.spiritual_traditions
        const customTraditions = traditions.filter(t => t.is_custom).map(t => t.title)
        const systemTraditions = traditions.filter(t => !t.is_custom)

        setOtherTraditions(customTraditions)
        setSelectedTraditions(systemTraditions)
      }

      // Initialize ethnic backgrounds from user data (multi-select)
      if (user.ethnic_backgrounds && user.ethnic_backgrounds.length > 0) {
        const backgrounds = user.ethnic_backgrounds
        const customBackgrounds = backgrounds.filter(b => b.is_custom).map(b => b.title)
        const systemBackgrounds = backgrounds.filter(b => !b.is_custom)

        setOtherBackgrounds(customBackgrounds)
        setSelectedBackgrounds(systemBackgrounds)
      }

      // Initialize privacy settings from user data
      // For own profile, these should be string values ("everyone" or "friends")
      setPrivacySettings({
        practiceRitualsVisibilityAccess:
          typeof user.practice_rituals_visibility_access === "string"
            ? user.practice_rituals_visibility_access
            : "everyone",
        teachingRitualsVisibilityAccess:
          typeof user.teaching_rituals_visibility_access === "string"
            ? user.teaching_rituals_visibility_access
            : "everyone",
        attendedEventsVisibilityAccess:
          typeof user.attended_events_visibility_access === "string"
            ? user.attended_events_visibility_access
            : "everyone",
        statsVisibilityAccess:
          typeof user.stats_visibility_access === "string"
            ? user.stats_visibility_access
            : "everyone",
        coachVisibilityAccess:
          typeof user.coach_visibility_access === "string"
            ? user.coach_visibility_access
            : "everyone",
      })

      // Reset form with user data from auth store
      form.reset(getDefaultValues())
    }
  }, [user, form, getDefaultValues])

  // Tag handlers
  const handleAddTag = useCallback(
    (tag: string) => {
      if (tags.length >= 10) {
        toast.error(t("editProfile.addTagError"))
        return
      }
      if (tag.length > 20) {
        toast.error(t("editProfile.tagLengthError"))
        return
      }
      if (!tags.includes(tag)) {
        setTags([...tags, tag])
      }
    },
    [tags, t],
  )

  const handleRemoveTag = useCallback(
    (tag: string) => {
      const updatedTags = tags.filter(t => t !== tag)
      setTags(updatedTags)

      // Track removed tags that were previously saved
      const matchedTag = user?.tags?.find(t => t.name === tag)
      if (matchedTag) {
        setRemovedTags([...removedTags, matchedTag.id])
      }
    },
    [tags, user, removedTags],
  )

  // Format user's existing location for display
  const formatLocationFromUser = useCallback((): string | undefined => {
    // If user has selected a location (from autocomplete), show the full address
    if (locationData?.address) {
      return locationData.address
    }

    // Otherwise, show full address from user profile
    if (!user) return undefined

    const parts = [user.street_address, user.city, user.state, user.country].filter(Boolean)

    return parts.length > 0 ? parts.join(", ") : undefined
  }, [user, locationData])

  // Initialize location data from user on mount
  useEffect(() => {
    if (user && (user.city || user.state || user.country)) {
      setLocationData({
        address: [user.street_address, user.city, user.state, user.country]
          .filter(Boolean)
          .join(", "),
        lat: user.latitude || 0,
        lng: user.longitude || 0,
        city: user.city || undefined,
        state: user.state || undefined,
        country: user.country || undefined,
        zipCode: user.zip_code || undefined,
      })
    }
  }, [user])

  // Handle location selection from autocomplete
  const handleLocationSelect = useCallback(
    (location: LocationData) => {
      setLocationData(location)
      // Update form values for immediate reflection
      form.setValue("city", location.city || "")
      form.setValue("streetAddress", location.address || "")
      form.setValue("state", location.state || "")
      form.setValue("country", location.country || "")
      form.setValue("zipCode", location.zipCode || "")
      form.setValue("latitude", location.lat)
      form.setValue("longitude", location.lng)
    },
    [form],
  )

  const handleCancel = () => {
    navigate(-1) // Go back to the previous page
  }

  // User should already be in auth store from login/signup
  // Watch specific form fields for validation (not all values to avoid infinite loop)
  const watchedName = form.watch("name")
  const watchedUsername = form.watch("username")
  const watchedBio = form.watch("bio")
  const watchedWebsite = form.watch("website")
  const watchedCity = form.watch("city")

  // Update section validation when form values or state changes
  useEffect(() => {
    const watchedValues = {
      name: watchedName,
      username: watchedUsername,
      bio: watchedBio,
      website: watchedWebsite,
    }
    const profileInfoValidation = validateProfileInfoSection(watchedValues, watchedCity)
    const photosValidation = validatePhotosSection(coverPhotos.length, galleryPhotos.length)
    const traditionsValidation = validateTraditionsSection(
      selectedTraditions,
      selectedBackgrounds,
      otherTraditions,
      otherBackgrounds,
    )
    const intentionsValidation = validateIntentionsSection(selectedIntentions)
    const privacyValidation = validatePrivacySection(privacySettings)

    setSectionValidation({
      profileInfo: profileInfoValidation,
      photos: photosValidation,
      traditions: traditionsValidation,
      intentions: intentionsValidation,
      privacy: privacyValidation,
    })
  }, [
    watchedName,
    watchedUsername,
    watchedBio,
    watchedWebsite,
    selectedTraditions,
    selectedBackgrounds,
    selectedIntentions,
    privacySettings,
    otherTraditions,
    otherBackgrounds,
    watchedCity,
    coverPhotos.length,
    galleryPhotos.length,
  ])

  // Fetch traditions and backgrounds data
  const { traditions, isLoading: traditionsLoading } = useSpiritualTraditions()
  const { backgrounds, isLoading: backgroundsLoading } = useEthnicBackgrounds()
  const { intentions: intentionsList, isLoading: intentionsLoading } = useIntentions(user?.id ?? "")

  // Initialize selected intentions from API response (which has is_checked field)
  // This ensures sync between Edit Profile and Intention Detail pages
  useEffect(() => {
    if (intentionsList.length > 0) {
      const checkedIds = intentionsList.filter(i => i.is_checked).map(i => i.id)
      setSelectedIntentions(checkedIds)
    }
  }, [intentionsList])

  // Build accordion items
  // biome-ignore lint/correctness/useExhaustiveDependencies: handleEditGalleryPhoto and handleGalleryCropComplete are stable useCallbacks; adding them causes infinite re-renders
  const accordionItems: AccordionItem[] = useMemo(() => {
    // Get traditions summary with names - use fetched data to map IDs to names (multi-select)
    // Also include custom "Others" values
    const traditionsSummary = getTraditionsSummary(
      selectedTraditions,
      selectedBackgrounds,
      traditions.map(t => ({ id: t.id, title: t.title })),
      backgrounds.map(b => ({ id: b.id, title: b.title })),
      otherTraditions,
      otherBackgrounds,
    )

    // Get intentions summary with names - use fetched data to map IDs to names
    const intentionsSummary = getIntentionsSummary(
      selectedIntentions,
      intentionsList.map(i => ({ id: i.id, title: i.title })),
    )

    const isLoadingAny = traditionsLoading || backgroundsLoading || intentionsLoading

    return [
      {
        id: "profile-info",
        title: t("editProfile.details"),
        isComplete: sectionValidation.profileInfo.isComplete,
        summary: sectionValidation.profileInfo.summary,
        showNextButton: true,
        content: (
          <div className="">
            <p className="text-[14px] font-monteserat  mt-1 mb-7 text-body">
              {t("editProfile.title")}
            </p>

            {/* Row 1: Name + Username (2 columns) */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-7">
              <CustomInput
                label={t("editProfile.fields.name")}
                type="text"
                required={true}
                placeholder={t("editProfile.fields.name")}
                {...form.register("name")}
                error={form.formState.errors.name?.message as string | undefined}
                disabled={form.formState.isSubmitting}
                onClearError={() => form.clearErrors("name")}
              />

              <CustomInput
                label={t("editProfile.fields.username")}
                prefix="@"
                required={true}
                placeholder={t("editProfile.fields.username")}
                {...form.register("username")}
                error={form.formState.errors.username?.message as string | undefined}
                disabled={form.formState.isSubmitting}
                onClearError={() => form.clearErrors("username")}
              />
            </div>

            {/* Row 2: Location (full width) */}
            <div className="mb-6">
              <AutocompleteInput
                label={t("editProfile.fields.location")}
                required={true}
                mode="full"
                placeholder={t("editProfile.fields.location")}
                value={formatLocationFromUser()}
                onLocationSelect={handleLocationSelect}
                error={form.formState.errors.city?.message as string | undefined}
                onClearError={() => form.clearErrors("city")}
              />
            </div>

            {/* Row 3: Description (full width) */}
            <div className="mb-5">
              <CustomTextarea
                label={t("editProfile.fields.bio")}
                required={true}
                rows={2}
                {...form.register("bio")}
                error={form.formState.errors.bio?.message as string | undefined}
                disabled={form.formState.isSubmitting}
                onClearError={() => form.clearErrors("bio")}
                placeholder={t("editProfile.fields.bio")}
              />
            </div>

            {/* Row 4: Tags (full width) */}
            <div className="mb-7">
              <TagsInput
                id="tags"
                label={t("editProfile.tagsLabel")}
                tags={tags}
                onAddTag={handleAddTag}
                onRemoveTag={handleRemoveTag}
                error={form.formState.errors.tags?.message as string | undefined}
                onClearError={() => form.clearErrors("tags")}
                disabled={form.formState.isSubmitting}
                enableSearch={true}
              />
            </div>

            {/* Row 5: Website + Twitter (2 columns) */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-7">
              <CustomInput
                label={t("editProfile.fields.website")}
                placeholder={t("editProfile.fields.website")}
                error={form.formState.errors.website?.message}
                onClearError={() => form.clearErrors("website")}
                {...form.register("website")}
              />

              <CustomInput
                label={t("editProfile.fields.twitter")}
                placeholder={t("editProfile.fields.twitter")}
                error={form.formState.errors.twitterUrl?.message}
                onClearError={() => form.clearErrors("twitterUrl")}
                {...form.register("twitterUrl")}
              />
            </div>

            {/* Row 6: Instagram + Facebook (2 columns) */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              <CustomInput
                label={t("editProfile.fields.instagram")}
                placeholder={t("editProfile.fields.instagram")}
                error={form.formState.errors.instagramUrl?.message}
                onClearError={() => form.clearErrors("instagramUrl")}
                {...form.register("instagramUrl")}
              />

              <CustomInput
                label={t("editProfile.fields.facebook")}
                placeholder={t("editProfile.fields.facebook")}
                error={form.formState.errors.facebookUrl?.message}
                onClearError={() => form.clearErrors("facebookUrl")}
                {...form.register("facebookUrl")}
              />
            </div>
          </div>
        ),
      },
      {
        id: "photos",
        title: t("editProfile.photos"),
        isComplete: sectionValidation.photos.isComplete,
        summary: sectionValidation.photos.summary,
        showNextButton: true,
        content: (
          <div className="">
            <p className="text-sm mb-7 text-body">{t("editProfile.uploadPhotos")}</p>

            {/* Inline validation error */}
            {sectionErrors.photos && (
              <p className="text-sm text-brand-terracotta-bright mb-2">{sectionErrors.photos}</p>
            )}

            {photosLoading || videoLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            ) : (
              <>
                {/* Hidden file input for regular photos */}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept={PROFILE_PHOTO_LIMITS.ALLOWED_TYPES.join(",")}
                  multiple
                  onChange={handleFileUpload}
                  className="hidden"
                  disabled={isUploading}
                />

                {/* Hidden file input for cover image */}
                <input
                  ref={coverInputRef}
                  type="file"
                  accept={PROFILE_PHOTO_LIMITS.ALLOWED_TYPES.join(",")}
                  onChange={handleCoverUpload}
                  className="hidden"
                  disabled={isCoverUploading}
                />

                <div>
                  {/* Profile Image Upload - shows cover photo only */}
                  <ProfileImageUpload
                    src={coverPhotos[0]?.src}
                    onClick={handleCoverUploadClick}
                    onDelete={() => handleDeletePhoto(coverPhotos[0]?.id)}
                    isUploading={isCoverUploading}
                  />

                  {/* Video Upload Section */}
                  <div className="pt-3">
                    <p className="font-semibold text-primary font-monteserat text-[18px]">
                      Your Introduction Video
                    </p>
                    <p className="text-body font-monteserat text-[14px] mb-6">
                      Share a short video about yourself, your background, and the rituals you guide
                      or practice.
                    </p>

                    {/* Upload area or video preview */}
                    {!video ? (
                      <div className="">
                        <button
                          type="button"
                          onClick={handleVideoUploadClick}
                          disabled={isVideoUploading}
                          className="flex justify-between items-center w-full border-1 border-dashed border-brand-slate-alpha-70 rounded-lg p-4 text-center cursor-pointer bg-water-light-variant hover:bg-accent/5 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <div className="flex justify-center items-center gap-2">
                            <div className="bg-brand-slate-alpha-70 p-2 flex justify-center items-center rounded-full">
                              <ArrowUpToLine className="w-4 h-4 text-white" />
                            </div>
                            <p className="font-semibold text-primary text-[15px]">
                              {isVideoUploading
                                ? t("editProfile.uploading")
                                : t("editProfile.uploadVideo")}
                            </p>
                          </div>

                          <p className="text-sm text-primary">
                            {t("editProfile.videoLimit", {
                              max: PROFILE_MEDIA_LIMITS.VIDEOS.MAX_FILES,
                              size: `${PROFILE_MEDIA_LIMITS.VIDEOS.MAX_FILE_SIZE / (1024 * 1024 * 1024)}GB`,
                            })}
                          </p>
                        </button>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {/* Video preview - mobile full width with top-right buttons */}
                        <div className="relative md:hidden group">
                          <div className="w-full h-52 bg-themed rounded-lg overflow-hidden relative">
                            <ProfileVideoPlayer
                              userId={user?.id || ""}
                              video={video}
                              className="h-full w-full"
                            />
                          </div>
                          {/* Mobile: Edit and Delete buttons on top right */}
                          <div className="absolute top-2 right-2 flex items-center gap-2 z-20 transition-opacity">
                            <button
                              type="button"
                              onClick={handleEditVideoClick}
                              className="p-2 rounded-full bg-black/50 text-white cursor-pointer"
                              aria-label="Edit video"
                            >
                              <PencilEditIcon size={16} />
                            </button>
                            <button
                              type="button"
                              onClick={handleDeleteVideo}
                              className="p-2 rounded-full bg-black/50 text-white cursor-pointer"
                              aria-label="Delete video"
                            >
                              <TrashDeleteIcon size={16} />
                            </button>
                          </div>
                        </div>
                        {/* Desktop: native player + edit/delete controls */}
                        <div className="hidden md:block">
                          <div className="relative">
                            <div className="h-64 w-full overflow-hidden rounded-lg bg-themed">
                              <ProfileVideoPlayer
                                userId={user?.id || ""}
                                video={video}
                                className="h-full w-full"
                              />
                            </div>
                            <div className="absolute top-2 right-2 z-20 flex items-center gap-2">
                              <button
                                type="button"
                                onClick={handleEditVideoClick}
                                className="cursor-pointer rounded-full bg-black/50 p-2 text-white"
                                aria-label="Replace video"
                              >
                                <PencilEditIcon size={16} />
                              </button>
                              <button
                                type="button"
                                onClick={handleDeleteVideo}
                                className="cursor-pointer rounded-full bg-black/50 p-2 text-white"
                                aria-label="Delete video"
                              >
                                <TrashDeleteIcon size={16} />
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    <VideoUploadModal
                      open={videoUploadModalOpen}
                      onOpenChange={setVideoUploadModalOpen}
                      onUpload={handleModalVideoUpload}
                      maxFileSize={PROFILE_MEDIA_LIMITS.VIDEOS.MAX_FILE_SIZE}
                      allowedTypes={[...PROFILE_MEDIA_LIMITS.VIDEOS.ALLOWED_TYPES]}
                      title={video ? "Replace Introduction Video" : "Upload Introduction Video"}
                      isUploading={isVideoUploading || isChangingVideo}
                      existingVideo={!!video}
                    />
                  </div>

                  <div className="mt-7 mb-2.5 md:mb-4">
                    <p className="font-semibold text-primary font-monteserat text-[18px]">
                      Photo Gallery
                    </p>
                    <p className="text-body font-monteserat text-[14px] mb-5 md:mb-6">
                      Upload up to 3 photos to your profile.
                    </p>

                    {/* Gallery Upload Button */}
                    <button
                      type="button"
                      onClick={handleUploadClick}
                      disabled={isUploading}
                      className="mb-2.5 md:mb-3 flex justify-between items-center w-full border-1 border-dashed border-brand-slate-alpha-70 rounded-lg p-4 text-center cursor-pointer bg-water-light-variant hover:bg-accent/5 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <div className="flex justify-center items-center gap-2">
                        <div className="bg-brand-slate-alpha-70 p-2 flex justify-center items-center rounded-full">
                          <ArrowUpToLine className="w-4 h-4 text-white" />
                        </div>
                        <p className="font-semibold text-primary text-[15px]">
                          {isUploading ? t("editProfile.uploading") : t("editProfile.uploadMedia")}
                        </p>
                      </div>

                      <p className="text-sm text-primary hidden md:flex">
                        {t("editProfile.fileLimit", {
                          max: PROFILE_PHOTO_LIMITS.MAX_GALLERY_PHOTOS,
                        })}
                      </p>
                    </button>
                    <p className="text-[10px] leading-5 text-[#F2F0EB80] md:hidden flex justify-end">
                      {t("editProfile.fileLimit", {
                        max: PROFILE_PHOTO_LIMITS.MAX_GALLERY_PHOTOS,
                      })}
                    </p>
                  </div>
                </div>

                {orderedGallery.length > 0 && (
                  <div className="space-y-4 mb-5">
                    {/* Photo grid - gallery photos only (non-cover), sortable */}
                    <DndContext
                      sensors={photoSensors}
                      collisionDetection={closestCenter}
                      onDragEnd={handlePhotoDragEnd}
                    >
                      <SortableContext
                        items={orderedGallery.map(p => p.id)}
                        strategy={rectSortingStrategy}
                      >
                        <div className="grid grid-cols-3 lg:grid-cols-5 gap-2 md:gap-4">
                          {orderedGallery.map(photo => (
                            <SortablePhotoItem
                              key={photo.id}
                              photo={photo}
                              onRemove={handleDeletePhoto}
                              onSetCover={handleSetCover}
                              onEdit={handleEditGalleryPhoto}
                            />
                          ))}
                        </div>
                      </SortableContext>
                    </DndContext>
                  </div>
                )}

                <MediaUploadModal
                  open={uploadModalOpen}
                  onOpenChange={setUploadModalOpen}
                  onUpload={handleModalUpload}
                  maxFiles={PROFILE_MEDIA_LIMITS.IMAGES.MAX_GALLERY_PHOTOS}
                  maxImageFileSize={PROFILE_MEDIA_LIMITS.IMAGES.MAX_FILE_SIZE}
                  allowedTypes={[...PROFILE_MEDIA_LIMITS.IMAGES.ALLOWED_TYPES]}
                  title="Upload Photos"
                  isUploading={isUploading}
                  existingFilesCount={galleryPhotos.length}
                  cropperType="profile"
                />

                <MediaUploadModal
                  open={coverCropperModalOpen}
                  onOpenChange={setCoverCropperModalOpen}
                  onUpload={async files => {
                    await handleCoverCropperComplete(files[0])
                  }}
                  maxFiles={1}
                  maxImageFileSize={PROFILE_MEDIA_LIMITS.IMAGES.MAX_FILE_SIZE}
                  allowedTypes={[...PROFILE_MEDIA_LIMITS.IMAGES.ALLOWED_TYPES]}
                  title="Center Your Profile Picture"
                  isUploading={isCoverUploading}
                  existingFilesCount={0}
                  cropperType="profile"
                  initialFile={selectedCoverFile || undefined}
                />

                <ImageCropperModal
                  open={!!croppingPhoto}
                  onOpenChange={open => {
                    if (!open) setCroppingPhoto(null)
                  }}
                  imageFile={null}
                  imageUrl={croppingPhoto?.src ?? null}
                  onCropComplete={handleGalleryCropComplete}
                  title="Crop your photo"
                />
              </>
            )}
          </div>
        ),
      },
      {
        id: "traditions",
        title: t("editProfile.spiritualTraditionsAndBackgrounds"),
        isComplete: sectionValidation.traditions.isComplete,
        summary: traditionsSummary || sectionValidation.traditions.summary,
        showNextButton: true,
        content: (
          <div className="space-y-4">
            <p className="text-[14px] font-montserat mb-7 mt-2 text-body">
              {t("editProfile.locationHelp")}
            </p>

            {/* Inline validation error */}
            {sectionErrors.traditions && (
              <p className="text-sm text-brand-terracotta-bright mb-2">
                {sectionErrors.traditions}
              </p>
            )}

            {isLoadingAny ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            ) : (
              <>
                {/* Spiritual Traditions - Full Width */}
                <div>
                  <h3 className="text-[15px] font-medium text-primary mb-3">
                    {t("editProfile.spiritualTraditions")}
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {traditions.map(tradition => (
                      <Button
                        key={tradition.id}
                        variant={
                          selectedTraditions.some(t => t.id === tradition.id)
                            ? "primary"
                            : "outline"
                        }
                        onClick={() => {
                          if (selectedTraditions.some(t => t.id === tradition.id)) {
                            // Remove - track for soft delete
                            setRemovedTraditions(prev => [...prev, tradition.id])
                            setSelectedTraditions(prev => prev.filter(t => t.id !== tradition.id))
                          } else {
                            // Add - remove from removed list
                            setSelectedTraditions(prev => [...prev, tradition])
                            setRemovedTraditions(prev => prev.filter(id => id !== tradition.id))
                          }
                        }}
                        disabled={updateProfileMutation.isPending}
                        className="rounded-md py-0 px-2 h-10 text-sm font-medium border-white"
                      >
                        {tradition.title}
                      </Button>
                    ))}
                  </div>
                  {traditions.length === 0 && !traditionsLoading && (
                    <p className="text-sm text-muted-foreground">
                      {t("editProfile.noTraditionsAvailable")}
                    </p>
                  )}
                </div>

                {/* Custom traditions input */}
                <div className="mt-7">
                  <CustomInput
                    label={t("editProfile.other")}
                    type="text"
                    placeholder={t("editProfile.fields.spiritualTraditionPlaceholder")}
                    value={otherTraditions[0] || ""}
                    onChange={e => {
                      const newValue = e.target.value
                      setOtherTraditions(prev => {
                        const newArr = [...prev]
                        newArr[0] = newValue
                        return newArr
                      })
                    }}
                    disabled={updateProfileMutation.isPending}
                  />
                </div>

                {/* Ethnic Backgrounds - Full Width */}
                <div className="mt-10">
                  <h3 className="text-[15px] font-medium text-primary mb-3">
                    {t("editProfile.ethnicBackgrounds")}
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {backgrounds.map(background => (
                      <Button
                        key={background.id}
                        variant={
                          selectedBackgrounds.some(b => b.id === background.id)
                            ? "primary"
                            : "outline"
                        }
                        onClick={() => {
                          if (selectedBackgrounds.some(b => b.id === background.id)) {
                            // Remove - track for soft delete
                            setRemovedBackgrounds(prev => [...prev, background.id])
                            setSelectedBackgrounds(prev => prev.filter(b => b.id !== background.id))
                          } else {
                            // Add - remove from removed list
                            setSelectedBackgrounds(prev => [...prev, background])
                            setRemovedBackgrounds(prev => prev.filter(id => id !== background.id))
                          }
                        }}
                        disabled={updateProfileMutation.isPending}
                        className="rounded-md py-0 px-2 h-10 text-sm font-medium border-white"
                      >
                        {background.title}
                      </Button>
                    ))}
                  </div>
                  {backgrounds.length === 0 && !backgroundsLoading && (
                    <p className="text-sm text-muted-foreground">
                      {t("editProfile.noEthnicBackgrounds")}
                    </p>
                  )}
                </div>

                {/* Custom backgrounds input */}
                <div className="mt-7 mb-7">
                  <CustomInput
                    label={t("editProfile.other")}
                    type="text"
                    placeholder={t("editProfile.fields.ethnicBackgroundPlaceholder")}
                    value={otherBackgrounds[0] || ""}
                    onChange={e => {
                      const newValue = e.target.value
                      setOtherBackgrounds(prev => {
                        const newArr = [...prev]
                        newArr[0] = newValue
                        return newArr
                      })
                    }}
                    disabled={updateProfileMutation.isPending}
                  />
                </div>
              </>
            )}
          </div>
        ),
      },
      {
        id: "intentions",
        title: t("editProfile.intentions"),
        isComplete: sectionValidation.intentions.isComplete,
        summary: intentionsSummary || sectionValidation.intentions.summary,
        showNextButton: true,
        content: (
          <div className="space-y-4">
            <p className="text-sm mb-3 mt-1 text-body">{t("editProfile.intentionsHelp")}</p>

            {/* Inline validation error */}
            {sectionErrors.intentions && (
              <p className="text-sm text-brand-terracotta-bright mb-2">
                {sectionErrors.intentions}
              </p>
            )}

            {intentionsLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            ) : (
              <div className="">
                {intentionsList.map(intention => (
                  <label
                    key={intention.id}
                    className={`flex items-start gap-3 py-3 rounded-lg cursor-pointer transition-colors`}
                  >
                    <input
                      type="checkbox"
                      checked={selectedIntentions.includes(intention.id)}
                      onChange={() => {
                        if (selectedIntentions.includes(intention.id)) {
                          const newlyRemoved = selectedIntentions.filter(i => i !== intention.id)
                          setRemovedIntentions([...removedIntentions, intention.id])
                          setSelectedIntentions(newlyRemoved)
                        } else {
                          setSelectedIntentions([...selectedIntentions, intention.id])
                        }
                      }}
                      disabled={updateProfileMutation.isPending}
                      className="mt-1 h-6 w-6 rounded border-brand-olive text-primary focus:ring-primary"
                    />
                    <div className="flex-1">
                      <span className="font-medium text-primary">{intention.title}</span>
                      {intention.description && (
                        <p className="text-sm text-muted-foreground mt-0.5">
                          {intention.description}
                        </p>
                      )}
                    </div>
                  </label>
                ))}
              </div>
            )}
            {intentionsList.length === 0 && !intentionsLoading && (
              <p className="text-sm text-muted-foreground">{t("editProfile.noIntentions")}</p>
            )}
          </div>
        ),
      },
      {
        id: "privacy",
        title: t("editProfile.privacy"),
        isComplete: sectionValidation.privacy.isComplete,
        summary: sectionValidation.privacy.summary,
        content: (
          <div className="space-y-4">
            <p className="text-sm mb-3 mt-2 text-body">{t("editProfile.controlVisibility")}</p>

            <div className="space-y-3.75 md:space-y-1">
              {/* Practice Rituals Visibility */}
              <div className="flex flex-col md:flex-row gap-1.25 md:items-center md:justify-between">
                <label className="text-sm font-medium text-primary">
                  {t("editProfile.visibility.practiceRituals")}
                </label>
                <Select
                  value={privacySettings.practiceRitualsVisibilityAccess}
                  onValueChange={value =>
                    setPrivacySettings({
                      ...privacySettings,
                      practiceRitualsVisibilityAccess: value,
                    })
                  }
                  disabled={updateProfileMutation.isPending}
                >
                  <SelectTrigger className="w-[140px] bg-[#F2F0EB1A] rounded-full md:rounded-none md:bg-transparent">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="everyone">{t("editProfile.everyone")}</SelectItem>
                    <SelectItem value="friends">{t("editProfile.friendsOnly")}</SelectItem>
                    <SelectItem value="only_me">{t("editProfile.onlyMe")}</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Teaching Rituals Visibility */}
              <div className="flex flex-col md:flex-row gap-1.25 md:items-center md:justify-between">
                <label className="text-sm font-medium text-primary">
                  {t("editProfile.visibility.teachingRituals")}
                </label>
                <Select
                  value={privacySettings.teachingRitualsVisibilityAccess}
                  onValueChange={value =>
                    setPrivacySettings({
                      ...privacySettings,
                      teachingRitualsVisibilityAccess: value,
                    })
                  }
                  disabled={updateProfileMutation.isPending}
                >
                  <SelectTrigger className="w-[140px] bg-[#F2F0EB1A] rounded-full md:rounded-none md:bg-transparent">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="everyone">{t("editProfile.everyone")}</SelectItem>
                    <SelectItem value="friends">{t("editProfile.friendsOnly")}</SelectItem>
                    <SelectItem value="only_me">{t("editProfile.onlyMe")}</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Attended Events Visibility */}
              <div className="flex flex-col md:flex-row gap-1.25 md:items-center md:justify-between">
                <label className="text-sm font-medium text-primary">
                  {t("editProfile.visibility.attendedEvents")}
                </label>
                <Select
                  value={privacySettings.attendedEventsVisibilityAccess}
                  onValueChange={value =>
                    setPrivacySettings({
                      ...privacySettings,
                      attendedEventsVisibilityAccess: value,
                    })
                  }
                  disabled={updateProfileMutation.isPending}
                >
                  <SelectTrigger className="w-[140px] bg-[#F2F0EB1A] rounded-full md:rounded-none md:bg-transparent">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="everyone">{t("editProfile.everyone")}</SelectItem>
                    <SelectItem value="friends">{t("editProfile.friendsOnly")}</SelectItem>
                    <SelectItem value="only_me">{t("editProfile.onlyMe")}</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Stats Visibility */}
              <div className="flex flex-col md:flex-row gap-1.25 md:items-center md:justify-between">
                <label className="text-sm font-medium text-primary">
                  {t("editProfile.visibility.stats")}
                </label>
                <Select
                  value={privacySettings.statsVisibilityAccess}
                  onValueChange={value =>
                    setPrivacySettings({
                      ...privacySettings,
                      statsVisibilityAccess: value,
                    })
                  }
                  disabled={updateProfileMutation.isPending}
                >
                  <SelectTrigger className="w-[140px] bg-[#F2F0EB1A] rounded-full md:rounded-none md:bg-transparent">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="everyone">{t("editProfile.everyone")}</SelectItem>
                    <SelectItem value="friends">{t("editProfile.friendsOnly")}</SelectItem>
                    <SelectItem value="only_me">{t("editProfile.onlyMe")}</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Coach Visibility */}
              <div className="flex flex-col md:flex-row gap-1.25 md:items-center md:justify-between">
                <label className="text-sm font-medium text-primary">
                  {t("editProfile.visibility.coach")}
                </label>
                <Select
                  value={privacySettings.coachVisibilityAccess}
                  onValueChange={value =>
                    setPrivacySettings({
                      ...privacySettings,
                      coachVisibilityAccess: value,
                    })
                  }
                  disabled={updateProfileMutation.isPending}
                >
                  <SelectTrigger className="w-[140px] bg-[#F2F0EB1A] rounded-full md:rounded-none md:bg-transparent">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="everyone">{t("editProfile.everyone")}</SelectItem>
                    <SelectItem value="friends">{t("editProfile.friendsOnly")}</SelectItem>
                    <SelectItem value="only_me">{t("editProfile.onlyMe")}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
        ),
      },
    ]
  }, [
    form,
    formatLocationFromUser,
    handleLocationSelect,
    tags,
    handleAddTag,
    handleRemoveTag,
    selectedTraditions,
    selectedBackgrounds,
    otherTraditions,
    otherBackgrounds,
    selectedIntentions,
    removedIntentions,
    privacySettings,
    updateProfileMutation.isPending,
    sectionValidation,
    traditions,
    traditionsLoading,
    backgrounds,
    backgroundsLoading,
    intentionsList,
    intentionsLoading,
    handleUploadClick,
    handleFileUpload,
    isUploading,
    photosLoading,
    videoLoading,
    handleSetCover,
    handleDeletePhoto,
    sectionErrors.photos,
    sectionErrors.traditions,
    sectionErrors.intentions,
    t,
    coverPhotos,
    galleryPhotos,
    handleModalUpload,
    uploadModalOpen,
    handleCoverUpload,
    handleCoverUploadClick,
    handleDeleteVideo,
    handleEditVideoClick,
    handleModalVideoUpload,
    handleVideoUploadClick,
    isVideoUploading,
    isChangingVideo,
    isCoverUploading,
    video,
    videoUploadModalOpen,
    coverCropperModalOpen,
    selectedCoverFile,
    handleCoverCropperComplete,
  ])

  // Handle Next button click - validate current section before moving to next
  const handleNext = async (currentIndex: number): Promise<boolean> => {
    const items = ["profile-info", "photos", "traditions", "intentions", "privacy"] as const
    const currentItemId = items[currentIndex]

    // Profile Info: Trigger form validation for name and username
    if (currentItemId === "profile-info") {
      const isValid = await form.trigger(["name", "username"])
      return isValid
    }

    // Photos: Validate at least 1 photo
    if (currentItemId === "photos") {
      if (photos.length === 0) {
        setSectionErrors(prev => ({
          ...prev,
          photos: t("editProfile.validation.addPhoto"),
        }))
        return false
      }
      setSectionErrors(prev => ({ ...prev, photos: "" }))
      return true
    }

    // Traditions: Validate at least one tradition OR background selected (including Others inputs)
    if (currentItemId === "traditions") {
      const hasTradition =
        selectedTraditions.length > 0 || otherTraditions.some(t => t && t.trim().length > 0)
      const hasBackground =
        selectedBackgrounds.length > 0 || otherBackgrounds.some(b => b && b.trim().length > 0)

      if (!hasTradition && !hasBackground) {
        setSectionErrors(prev => ({
          ...prev,
          traditions: t("editProfile.validation.selectTradition"),
        }))
        return false
      }
      setSectionErrors(prev => ({ ...prev, traditions: "" }))
      return true
    }

    // Intentions: Validate at least one intention selected
    if (currentItemId === "intentions") {
      if (selectedIntentions.length === 0) {
        setSectionErrors(prev => ({
          ...prev,
          intentions: t("editProfile.validation.selectIntention"),
        }))
        return false
      }
      setSectionErrors(prev => ({ ...prev, intentions: "" }))
      return true
    }

    // Privacy: Always valid (has defaults)
    return true
  }

  // Show login message if user is not authenticated
  if (!user) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <p>{t("pleaseLogin")}</p>
      </div>
    )
  }

  return (
    <div className="bg-background min-h-screen md:py-8">
      <LeaveConfirmationModal
        isOpen={unsavedGuard.isBlocked}
        onKeepEditing={unsavedGuard.cancelLeave}
        onLeave={unsavedGuard.confirmLeave}
      />
      <div className="max-w-360 mx-auto px-5 md:px-17.5">
        {/* Two Column Layout */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {/* Left Column - Form Sections (70%) */}
          <div className="md:col-span-2">
            <div id="edit-profile-accordion">
              <Accordion
                items={accordionItems}
                defaultOpenIndex={defaultOpenIndex}
                allowMultiple={false}
                onNextClick={handleNext}
              />
            </div>
          </div>

          {/* Right Column - Tips & Actions (30%) */}
          <div className="lg:col-span-1 space-y-6">
            {/* Tips Card */}
            <div className="hidden md:block rounded-xl border-1 bg-card border-card px-5 py-4">
              <h3 className="text-xl font-semibold mb-3 text-primary">{t("editProfile.tips")}</h3>
              <ul className="font-montserrat text-[14px] font-normal leading-[25px] text-primary list-none">
                <li className="flex items-start gap-2">
                  <span>.</span>
                  <span>{t("editProfile.tipsList.realName")}</span>
                </li>
                <li className="flex items-start gap-2">
                  <span>.</span>
                  <span>{t("editProfile.tipsList.shortBio")} </span>
                </li>
                <li className="flex items-start gap-2">
                  <span>.</span>
                  <span>{t("editProfile.tipsList.clearPhoto")}</span>
                </li>
                <li className="flex items-start gap-2">
                  <span>.</span>
                  <span>{t("editProfile.tipsList.comfortable")}</span>
                </li>
              </ul>
            </div>

            {/* Profile Completion Tips */}
            <ProfileCompletionTips
              user={user}
              selectedTraditions={selectedTraditions}
              selectedBackgrounds={selectedBackgrounds}
              otherTraditions={otherTraditions}
              otherBackgrounds={otherBackgrounds}
              selectedIntentions={selectedIntentions}
              profilePhotos={photos}
              formName={form.watch("name")}
              formUsername={form.watch("username")}
              formBio={form.watch("bio")}
              formCity={form.watch("city")}
            />

            {/* Action Buttons */}
            <ProfileActionButtons
              onSave={() => handleSubmit()}
              onCancel={handleCancel}
              isSubmitting={updateProfileMutation.isPending}
            />

            <p className="text-[14px] text-body">{t("editProfile.passwordSettings")}</p>
          </div>
        </div>
      </div>
    </div>
  )
}
